import React, { useState, useMemo, useEffect } from 'react';
import TableControl from './TableControl';
import SetPointsManagerTable from './SetPointsManagerTable';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useParameterGroups } from '../../hooks/useParameterGroups';
import './setpoints-manager.scss';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Loader from '../common/Loader/Loader';
import WriteConfirm from './WriteConfirm';
import { OperationType } from '../../types/SetpointManagerTypes';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { useToastService } from '../../components/toast/ToastService';
import { ProtectComponent } from '../../rbac';
import { DASHBOARDS, R, useRBAC, WIDGETS } from '../../rbac';
import { setSelectedRows } from './parameterGroupsSlice';

const SetPointsManager: React.FC = () => {
  const {
    groups,
    loading: groupsLoading,
    error: groupsError,
    toggleGroupExpansion,
    toggleAll,
    toggleGlobalSelectAll,
    globalSelectAll,
    updateSortData,
    sortData,
    filters,
    updateFilters,
    readController,
    writeController,
    backupSetpoints,
    restoreSetpoints,
  } = useParameterGroups();

  const [showBackup, setShowBackup] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [writeConfirm, setWriteConfirm] = useState(false);
  const [confirmOperation, setConfirmOperation] = useState<OperationType>('write');
  const writeItems = useAppSelector((state) => state.parameterGroups.writeItems);
  const parameterGroupsLoading = useAppSelector((state) => state.parameterGroups.loading);
  const { showErrorToast } = useToastService();

  const resource = R.widget(DASHBOARDS.SETPOINTS, WIDGETS.SETPOINT_MANAGER);
  const { can } = useRBAC();
  const dispatch = useAppDispatch();

  const rPermission = {
    canReadSetpoints: can(resource, 'readSetpoints'),
    canWriteSetpoints: can(resource, 'writeSetpoints'),
    canBackupSetpoints: can(resource, 'backupSetpoints'),
    canRestoreSetpoints: can(resource, 'restoreSetpoints'),
    canShowBackup: can(resource, 'showBackup'),
    canViewSetpointHistory: can(resource, 'viewSetpointHistory'),
    canExport: can(resource, 'exportSetpointHistory'),
  }

  const globalFilterValue = useAppSelector((state) => state.globalFilter.value);

  const totalFilteredMatches = useMemo(() => {
    if (!globalFilterValue) return null;
    const filter = globalFilterValue.toLowerCase();
    return groups.reduce((acc, group) => {
      const matches = (group.setpoints || []).filter((param) =>
        [param.currentValue, param.description, param.parameter, param.uom]
          .map((attr) => (attr !== undefined && attr !== null ? String(attr).toLowerCase() : ''))
          .some((val) => val.includes(filter)),
      ).length;
      return acc + matches;
    }, 0);
  }, [groups, globalFilterValue]);

  // Get all available groups with unique IDs and display labels
  const availableGroups = useMemo(() => {
    return groups.map(group => ({
      id: group.setpointGroup,
      label: group.setpointGroupName,
      parameterCount: group.registerCount,
      groupName: group.setpointGroupName
    }));
  }, [groups]);

  // Initialize selected groups when groups are loaded
  React.useEffect(() => {
    if (groups.length > 0 && selectedGroupIds.length === 0) {
      setSelectedGroupIds(groups.map((group) => group.setpointGroup));
    }
  }, [groups, selectedGroupIds.length]);

  // Reset group selection when asset changes
  React.useEffect(() => {
    if (!parameterGroupsLoading) {
      setSelectedGroupIds(groups.map((group) => group.setpointGroup));
    }
  }, [parameterGroupsLoading]);

  // Filter groups based on selection
  const filteredGroups = useMemo(() => {
    if (selectedGroupIds.length === 0) {
      return groups;
    }
    return groups.filter(group => selectedGroupIds.includes(group.setpointGroup));
  }, [groups, selectedGroupIds]);

  const handleShowBackupToggle = (isActive: boolean) => {
    setShowBackup(isActive);
  };

  const handleFilterApply = (selectedGroupIds: string[]) => {
    setSelectedGroupIds(selectedGroupIds);
  };
  const toggleWriteConfirm = () => {
    setWriteConfirm((prev) => !prev);
  };
  const openWriteConfirmFor = (operation: OperationType) => {
    setConfirmOperation(operation);
    setWriteConfirm(true);
  };

  // Unmount cleanup effect
  useEffect(() => {
    return () => {
      // cleaned up selected Setpoint rows on unmount
      dispatch(setSelectedRows([]));
    };
  }, []);
  return (
    <ProtectComponent
      resource={R.widget(DASHBOARDS.SETPOINTS, WIDGETS.SETPOINT_MANAGER)}
      action='view'
      widgetName='Setpoints'
      widgetMessageName='setpoints'
    >
      <>
        <div className='setpoints-container main-container1 pb-8'>
          <div className='set-points-header'>
            <div className='set-points-header-title'>
              <h2>Setpoints</h2>
            </div>
            <div className='set-points-header-actions'>
              <button className='button-wrapper' onClick={() => toggleAll(true)}>
                <span className='button-icon'>
                  <ChevronDownIcon />
                </span>
                Expand all
              </button>
              <div className='line-seperator'></div>
              <button className='button-wrapper' onClick={() => toggleAll(false)}>
                <span className='button-icon'>
                  <ChevronUpIcon />
                </span>
                Collapse all
              </button>
            </div>
          </div>

          <TableControl
            onShowBackupToggle={handleShowBackupToggle}
            toggleGlobalSelectAll={toggleGlobalSelectAll}
            globalSelectAll={globalSelectAll}
            availableGroups={availableGroups}
            selectedGroupIds={selectedGroupIds}
            onFilterApply={handleFilterApply}
            readController={readController}
            handleWriteClick={() => {
              const anyEmpty = Object.values(writeItems).some((v) => v === '');
              if (anyEmpty) {
                showErrorToast('Empty values are not allowed.');
              } else {
                openWriteConfirmFor('write');
              }
            }}
            handleBackupClick={() => openWriteConfirmFor('backup')}
            handleRestoreClick={() => openWriteConfirmFor('restore')}
            rPermission={rPermission}
          />
          {groupsLoading ? (
            <main>
              <div className='sp-search-norecords'>
              <Loader />
              </div>
            </main>
          ) : groupsError ? (
            <main className='main-container'>
              <ErrorMessage message={groupsError} onRetry={() => window.location.reload()} />
            </main>
          ) : totalFilteredMatches === 0 && globalFilterValue ? (
            <div className='sp-search-norecords'>
              <span className='sp-search-norecords--message'>
                No records found for the given search criteria
              </span>
            </div>
          ) : (
            <SetPointsManagerTable
              showBackup={showBackup}
              groups={filteredGroups}
              toggleGroupExpansion={toggleGroupExpansion}
              updateSortData={updateSortData}
              sortData={sortData}
              updateFilters={updateFilters}
              filters={filters}
              rPermission={rPermission}
            />
          )}
        </div>
        <WriteConfirm
          open={writeConfirm}
          handleCancel={toggleWriteConfirm}
          groups={groups}
          onConfirm={(addressValues) =>
            confirmOperation === 'restore'
              ? restoreSetpoints(addressValues)
              : confirmOperation === 'backup'
                ? backupSetpoints(addressValues)
                : writeController(addressValues)
          }
          operation={confirmOperation}
        />
      </>
    </ProtectComponent>
  );
};

export default SetPointsManager;
